import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

const MAX_MB = 10;

const VerificacionUtm = () => {
  const navigate = useNavigate();

  const [archivoEnviado, setArchivoEnviado]     = useState(null);
  const [archivoDescuento, setArchivoDescuento] = useState(null);
  const [resultado, setResultado]               = useState(null);
  const [loading, setLoading]                   = useState(false);
  const [alerta, setAlerta]                     = useState({ mostrar: false, mensaje: "", tipo: "success" });

  const refEnviado   = useRef(null);
  const refDescuento = useRef(null);

  // ─── Alerta toast ────────────────────────────────────────────────────────────

  const mostrarAlerta = (mensaje, tipo = "danger") => {
    setAlerta({ mostrar: true, mensaje, tipo });
    setTimeout(() => setAlerta({ mostrar: false, mensaje: "", tipo: "success" }), 4000);
  };

  // ─── Reset ───────────────────────────────────────────────────────────────────

  const resetForm = () => {
    setArchivoEnviado(null);
    setArchivoDescuento(null);
    setResultado(null);
    if (refEnviado.current)   refEnviado.current.value   = "";
    if (refDescuento.current) refDescuento.current.value = "";
  };

  // ─── Validar tamaño ──────────────────────────────────────────────────────────

  const validarArchivo = (file, nombre) => {
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      mostrarAlerta(`El archivo "${nombre}" supera el límite de ${MAX_MB} MB.`);
      return null;
    }
    return file;
  };

  // ─── Submit ──────────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!archivoEnviado || !archivoDescuento) {
      mostrarAlerta("Debe seleccionar ambos archivos Excel.");
      return;
    }

    setLoading(true);
    setResultado(null);

    try {
      const [dataEnviado, dataDescuento] = await Promise.all([
        readExcelToJson(archivoEnviado),
        readExcelToJson(archivoDescuento),
      ]);

      const comparacion = compararTotales(dataEnviado, dataDescuento);
      setResultado(comparacion);

      // Mostrar advertencias de duplicados o filas inválidas si las hay
      const advertencias = [];
      if (comparacion.duplicadosEnviado.length > 0)
        advertencias.push(`Cédulas duplicadas en archivo enviado: ${comparacion.duplicadosEnviado.join(", ")}`);
      if (comparacion.duplicadosDescuento.length > 0)
        advertencias.push(`Cédulas duplicadas en archivo descuento: ${comparacion.duplicadosDescuento.join(", ")}`);
      if (comparacion.filasInvalidasEnviado.length > 0)
        advertencias.push(`Filas con total no numérico en archivo enviado: ${comparacion.filasInvalidasEnviado.map(f => `fila ${f.fila} (${f.cedula})`).join(", ")}`);
      if (comparacion.filasInvalidasDescuento.length > 0)
        advertencias.push(`Filas con total no numérico en archivo descuento: ${comparacion.filasInvalidasDescuento.map(f => `fila ${f.fila} (${f.cedula})`).join(", ")}`);

      if (advertencias.length > 0) {
        mostrarAlerta(advertencias.join(" | "), "warning");
      }

      const hayProblemas =
        comparacion.conDiferencia   > 0 ||
        comparacion.noEncontrados   > 0 ||
        comparacion.soloEnDescuento > 0;

      if (hayProblemas) {
        generarExcelDiferencias(comparacion);
      }
    } catch (err) {
      console.error("Error al procesar archivos:", err);
      mostrarAlerta(err.message || "No se pudieron procesar uno o ambos archivos Excel.");
    } finally {
      setLoading(false);
    }
  };

  // ─── Leer Excel → JSON ───────────────────────────────────────────────────────

  const readExcelToJson = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data     = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const firstSheet = workbook.SheetNames[0];
          const worksheet  = workbook.Sheets[firstSheet];
          const json = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "" });
          resolve({ rows: json, hoja: firstSheet });
        } catch {
          reject(new Error(`El archivo "${file.name}" tiene un formato no válido o está corrupto.`));
        }
      };
      reader.onerror = () => reject(new Error(`No se pudo leer el archivo "${file.name}".`));
      reader.readAsArrayBuffer(file);
    });
  };

  // ─── Detectar columnas por encabezado ────────────────────────────────────────

  const detectarColumnas = (headers, nombreArchivo) => {
    const norm = headers.map((h) => h.toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim());

    const idxCedula = norm.findIndex((h) => h.includes("cedul") || h === "ci" || h === "id");
    const idxNombre = norm.findIndex((h) => h.includes("nombre") || h.includes("apellido"));
    const idxTotal  = norm.findIndex((h) => h.includes("total") || h.includes("monto") || h.includes("valor"));

    if (idxCedula === -1) throw new Error(`"${nombreArchivo}": no se encontró columna de Cédula/CI.`);
    if (idxNombre === -1) throw new Error(`"${nombreArchivo}": no se encontró columna de Nombre.`);
    if (idxTotal  === -1) throw new Error(`"${nombreArchivo}": no se encontró columna de Total/Monto.`);

    return { idxCedula, idxNombre, idxTotal };
  };

  // ─── Normalizar cédula ───────────────────────────────────────────────────────

  const normalizarCedula = (valor) => {
    if (!valor) return "";
    return valor.toString().trim().replace(/[^0-9]/g, "").replace(/^0+/, "");
  };

  // ─── Comparar totales ────────────────────────────────────────────────────────

  const compararTotales = ({ rows: enviadoRows, hoja: hojaEnviado }, { rows: descuentoRows, hoja: hojaDescuento }) => {
    // ── Parsear archivo enviado ──
    const headersEnviado = enviadoRows[0] || [];
    const { idxCedula: ce, idxNombre: cn, idxTotal: ct } = detectarColumnas(headersEnviado, archivoEnviado.name);

    const mapEnviado            = new Map();
    const duplicadosEnviado     = [];
    const filasInvalidasEnviado = [];

    for (let i = 1; i < enviadoRows.length; i++) {
      const row    = enviadoRows[i];
      const cedula = normalizarCedula(row[ce]);
      if (!cedula) continue;

      const rawTotal = row[ct];
      const total    = parseFloat(rawTotal);
      if (isNaN(total)) {
        filasInvalidasEnviado.push({ cedula, fila: i + 1, valor: rawTotal });
        continue;
      }

      const nombre = (row[cn] || "").toString().trim();

      if (mapEnviado.has(cedula)) {
        if (!duplicadosEnviado.includes(cedula)) duplicadosEnviado.push(cedula);
      } else {
        mapEnviado.set(cedula, { total, nombre });
      }
    }

    // ── Parsear archivo descuento ──
    const headersDescuento = descuentoRows[0] || [];
    const { idxCedula: de, idxTotal: dt } = detectarColumnas(headersDescuento, archivoDescuento.name);

    const mapDescuento            = new Map();
    const duplicadosDescuento     = [];
    const filasInvalidasDescuento = [];

    for (let i = 1; i < descuentoRows.length; i++) {
      const row    = descuentoRows[i];
      const cedula = normalizarCedula(row[de]);
      if (!cedula) continue;

      const rawTotal = row[dt];
      const total    = parseFloat(rawTotal);
      if (isNaN(total)) {
        filasInvalidasDescuento.push({ cedula, fila: i + 1, valor: rawTotal });
        continue;
      }

      if (mapDescuento.has(cedula)) {
        if (!duplicadosDescuento.includes(cedula)) duplicadosDescuento.push(cedula);
      } else {
        mapDescuento.set(cedula, total);
      }
    }

    // ── Comparar ──
    const correctos       = [];
    const diferencias     = [];
    const noEncontrados   = [];   // en enviado, NO en descuento
    const soloEnDescuento = [];   // en descuento, NO en enviado

    for (const [cedula, info] of mapEnviado) {
      if (!mapDescuento.has(cedula)) {
        noEncontrados.push({ cedula, nombre: info.nombre, totalEnviado: info.total });
        continue;
      }
      const totalDesc = mapDescuento.get(cedula);
      if (Math.abs(info.total - totalDesc) < 0.01) {
        correctos.push({ cedula, nombre: info.nombre, total: info.total });
      } else {
        diferencias.push({
          cedula,
          nombre:         info.nombre,
          totalEnviado:   info.total,
          totalDescuento: totalDesc,
          diferencia:     info.total - totalDesc,
        });
      }
    }

    for (const [cedula, total] of mapDescuento) {
      if (!mapEnviado.has(cedula)) {
        soloEnDescuento.push({ cedula, totalDescuento: total });
      }
    }

    return {
      hojaEnviado,
      hojaDescuento,
      totalProcesados:        mapEnviado.size,
      correctos:              correctos.length,
      conDiferencia:          diferencias.length,
      noEncontrados:          noEncontrados.length,
      soloEnDescuento:        soloEnDescuento.length,
      diferencias,
      noEncontradosArray:     noEncontrados,
      soloEnDescuentoArray:   soloEnDescuento,
      duplicadosEnviado,
      duplicadosDescuento,
      filasInvalidasEnviado,
      filasInvalidasDescuento,
    };
  };

  // ─── Generar Excel de diferencias ────────────────────────────────────────────

  const generarExcelDiferencias = (data) => {
    const wb = XLSX.utils.book_new();

    if (data.conDiferencia > 0) {
      const ws = XLSX.utils.aoa_to_sheet([
        ["Cédula", "Nombre", "Total Enviado", "Total Descuento", "Diferencia"],
        ...data.diferencias.map((d) => [d.cedula, d.nombre, d.totalEnviado, d.totalDescuento, d.diferencia]),
      ]);
      XLSX.utils.book_append_sheet(wb, ws, "Diferencias");
    }

    if (data.noEncontrados > 0) {
      const ws = XLSX.utils.aoa_to_sheet([
        ["Cédula", "Nombre", "Total Enviado"],
        ...data.noEncontradosArray.map((d) => [d.cedula, d.nombre, d.totalEnviado]),
      ]);
      XLSX.utils.book_append_sheet(wb, ws, "No en Descuento");
    }

    if (data.soloEnDescuento > 0) {
      const ws = XLSX.utils.aoa_to_sheet([
        ["Cédula", "Total Descuento"],
        ...data.soloEnDescuentoArray.map((d) => [d.cedula, d.totalDescuento]),
      ]);
      XLSX.utils.book_append_sheet(wb, ws, "Solo en Descuento");
    }

    const fileName = `Diferencias_Totales_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // ─── Render ───────────────────────────────────────────────────────────────────

  const hayProblemas = resultado && (
    resultado.conDiferencia   > 0 ||
    resultado.noEncontrados   > 0 ||
    resultado.soloEnDescuento > 0
  );

  return (
    <div className="container-fluid bg-light min-vh-100 py-4 px-lg-5">

      {/* Toast */}
      {alerta.mostrar && (
        <div
          className={`alert alert-${alerta.tipo} alert-dismissible fade show shadow position-fixed`}
          style={{ top: "20px", right: "20px", zIndex: 9999, minWidth: "340px", maxWidth: "520px" }}
          role="alert"
        >
          <div className="d-flex align-items-start gap-2">
            <i className={`bi flex-shrink-0 mt-1 ${
              alerta.tipo === "success" ? "bi-check-circle-fill" :
              alerta.tipo === "warning" ? "bi-exclamation-triangle-fill" :
              "bi-x-circle-fill"
            }`}></i>
            <span style={{ fontSize: "0.875rem" }}>{alerta.mensaje}</span>
          </div>
          <button
            className="btn-close"
            onClick={() => setAlerta({ ...alerta, mostrar: false })}
            aria-label="Cerrar"
          ></button>
        </div>
      )}

      {/* Header */}
      <div className="card shadow-sm border-0 mb-4 border-start border-success border-4">
        <div className="card-body d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div className="d-flex align-items-center gap-3">
            <button
              className="btn btn-outline-success border-0 rounded-circle p-2"
              onClick={() => navigate("/DashboardSecretaria")}
              aria-label="Volver"
            >
              <i className="bi bi-arrow-left-short fs-4"></i>
            </button>
            <h4 className="fw-bold text-dark mb-0">
              <i className="bi bi-file-earmark-excel me-2 text-success"></i>
              Verificación de Totales UTM
            </h4>
          </div>
        </div>
      </div>

      {/* Formulario */}
      <div className="card shadow-sm border-0 mb-4">
        <div className="card-body">
          <h5 className="fw-bold mb-1 text-success">Comparar archivos</h5>
          <p className="text-muted small mb-4">
            Procesamiento local en el navegador — ningún dato se envía al servidor.<br />
            Los archivos deben tener columnas <strong>Cédula</strong>, <strong>Nombre</strong> y <strong>Total</strong> (el nombre exacto no importa, se detectan automáticamente). Tamaño máximo por archivo: {MAX_MB} MB.
          </p>

          <form onSubmit={handleSubmit} noValidate>
            <div className="row g-3">

              <div className="col-md-6">
                <label className="form-label fw-semibold small">
                  Archivo enviado <span className="text-danger">*</span>
                </label>
                <input
                  ref={refEnviado}
                  type="file"
                  className="form-control"
                  accept=".xlsx,.xls"
                  disabled={loading}
                  onChange={(e) => {
                    const f = validarArchivo(e.target.files?.[0] || null, e.target.files?.[0]?.name);
                    setArchivoEnviado(f || null);
                    if (!f && refEnviado.current) refEnviado.current.value = "";
                  }}
                />
                <div className="form-text">Columnas requeridas: Cédula, Nombre, Total</div>
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold small">
                  Archivo con descuento aplicado <span className="text-danger">*</span>
                </label>
                <input
                  ref={refDescuento}
                  type="file"
                  className="form-control"
                  accept=".xlsx,.xls"
                  disabled={loading}
                  onChange={(e) => {
                    const f = validarArchivo(e.target.files?.[0] || null, e.target.files?.[0]?.name);
                    setArchivoDescuento(f || null);
                    if (!f && refDescuento.current) refDescuento.current.value = "";
                  }}
                />
                <div className="form-text">Columnas requeridas: Cédula, Total</div>
              </div>

              <div className="col-12 d-flex justify-content-end gap-2 mt-3">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={resetForm}
                  disabled={loading}
                >
                  <i className="bi bi-x-circle me-1"></i>Limpiar
                </button>
                <button
                  type="submit"
                  className="btn btn-success px-5"
                  disabled={loading || !archivoEnviado || !archivoDescuento}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Comparando…
                    </>
                  ) : (
                    <><i className="bi bi-arrow-left-right me-2"></i>Comparar archivos</>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Resultado */}
      {resultado && (
        <div className="card shadow-sm border-0">
          <div className="card-body">
            <h5 className="fw-bold mb-1 text-success">Resultado de la comparación</h5>

            {/* Hojas leídas */}
            <p className="text-muted small mb-4">
              Hoja leída — enviado: <strong>{resultado.hojaEnviado}</strong> &nbsp;|&nbsp;
              descuento: <strong>{resultado.hojaDescuento}</strong>
            </p>

            {/* Advertencias de calidad de datos */}
            {(resultado.duplicadosEnviado.length > 0 || resultado.duplicadosDescuento.length > 0 ||
              resultado.filasInvalidasEnviado.length > 0 || resultado.filasInvalidasDescuento.length > 0) && (
              <div className="alert alert-warning small mb-4">
                <p className="fw-semibold mb-1"><i className="bi bi-exclamation-triangle-fill me-1"></i>Advertencias de calidad de datos</p>
                <ul className="mb-0 ps-3">
                  {resultado.duplicadosEnviado.length > 0 && (
                    <li>Cédulas duplicadas en archivo <strong>enviado</strong> (se tomó la primera aparición): {resultado.duplicadosEnviado.join(", ")}</li>
                  )}
                  {resultado.duplicadosDescuento.length > 0 && (
                    <li>Cédulas duplicadas en archivo de <strong>descuento</strong> (se tomó la primera aparición): {resultado.duplicadosDescuento.join(", ")}</li>
                  )}
                  {resultado.filasInvalidasEnviado.length > 0 && (
                    <li>Filas omitidas por total no numérico en archivo <strong>enviado</strong>: {resultado.filasInvalidasEnviado.map(f => `fila ${f.fila}`).join(", ")}</li>
                  )}
                  {resultado.filasInvalidasDescuento.length > 0 && (
                    <li>Filas omitidas por total no numérico en archivo de <strong>descuento</strong>: {resultado.filasInvalidasDescuento.map(f => `fila ${f.fila}`).join(", ")}</li>
                  )}
                </ul>
              </div>
            )}

            {!hayProblemas ? (
              <div className="alert alert-success text-center mb-0 py-4">
                <i className="bi bi-check-circle-fill me-2 fs-4"></i>
                ¡Todos los totales coinciden perfectamente!
              </div>
            ) : (
              <>
                {/* Tarjetas resumen */}
                <div className="row text-center g-3 mb-4">
                  {[
                    { label: "Total procesados",   value: resultado.totalProcesados, color: "secondary" },
                    { label: "Coincidencias",       value: resultado.correctos,       color: "success"   },
                    { label: "Con diferencia",      value: resultado.conDiferencia,   color: "danger"    },
                    { label: "No en descuento",     value: resultado.noEncontrados,   color: "warning"   },
                    { label: "Solo en descuento",   value: resultado.soloEnDescuento, color: "info"      },
                  ].map(({ label, value, color }) => (
                    <div className="col" key={label}>
                      <div className={`p-3 rounded border border-${color} border-opacity-25 bg-white`}>
                        <small className={`d-block text-${color} fw-semibold`}>{label}</small>
                        <h4 className={`fw-bold text-${color} mb-0`}>{value}</h4>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Tabla de diferencias */}
                {resultado.conDiferencia > 0 && (
                  <div className="mb-4">
                    <h6 className="fw-bold text-danger mb-2">
                      <i className="bi bi-exclamation-circle me-1"></i>Registros con diferencia
                    </h6>
                    <div className="table-responsive">
                      <table className="table table-sm table-hover table-bordered align-middle small">
                        <thead className="table-danger">
                          <tr>
                            <th>Cédula</th>
                            <th>Nombre</th>
                            <th className="text-end">Total Enviado</th>
                            <th className="text-end">Total Descuento</th>
                            <th className="text-end">Diferencia</th>
                          </tr>
                        </thead>
                        <tbody>
                          {resultado.diferencias.map((d) => (
                            <tr key={d.cedula}>
                              <td>{d.cedula}</td>
                              <td>{d.nombre}</td>
                              <td className="text-end">{d.totalEnviado.toFixed(2)}</td>
                              <td className="text-end">{d.totalDescuento.toFixed(2)}</td>
                              <td className={`text-end fw-semibold ${d.diferencia > 0 ? "text-danger" : "text-success"}`}>
                                {d.diferencia > 0 ? "+" : ""}{d.diferencia.toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Tabla no encontrados */}
                {resultado.noEncontrados > 0 && (
                  <div className="mb-4">
                    <h6 className="fw-bold text-warning mb-2">
                      <i className="bi bi-search me-1"></i>En enviado, no en descuento
                    </h6>
                    <div className="table-responsive">
                      <table className="table table-sm table-hover table-bordered align-middle small">
                        <thead className="table-warning">
                          <tr>
                            <th>Cédula</th>
                            <th>Nombre</th>
                            <th className="text-end">Total Enviado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {resultado.noEncontradosArray.map((d) => (
                            <tr key={d.cedula}>
                              <td>{d.cedula}</td>
                              <td>{d.nombre}</td>
                              <td className="text-end">{d.totalEnviado.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Tabla solo en descuento */}
                {resultado.soloEnDescuento > 0 && (
                  <div className="mb-4">
                    <h6 className="fw-bold text-info mb-2">
                      <i className="bi bi-file-earmark-minus me-1"></i>En descuento, no en enviado
                    </h6>
                    <div className="table-responsive">
                      <table className="table table-sm table-hover table-bordered align-middle small">
                        <thead className="table-info">
                          <tr>
                            <th>Cédula</th>
                            <th className="text-end">Total Descuento</th>
                          </tr>
                        </thead>
                        <tbody>
                          {resultado.soloEnDescuentoArray.map((d) => (
                            <tr key={d.cedula}>
                              <td>{d.cedula}</td>
                              <td className="text-end">{d.totalDescuento.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="alert alert-info mb-0 small">
                  <i className="bi bi-download me-2"></i>
                  Se descargó automáticamente un archivo Excel con todas las diferencias y registros no encontrados.
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VerificacionUtm;